import { PageNavigation } from "../../../components/layout/PageNavigation";
import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import { Trans } from "react-i18next";import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../../components/ui/button";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { DayScheduleCard } from "../components";
import {
  fetchWeeklyWorkSchedule,
  saveWeeklyWorkSchedule } from
"../../../services/weeklyWorkScheduleService";
import {
  DAYS_OF_WEEK,
  WEEK_NUMBERS,
  SCHEDULE_MODES } from
"../constants/scheduleConfig";
import { Toast } from "../components";

/**
 * Per-day-of-week default state:
 * { mode: "All" | "Custom", allWeeksType: string, weekTypes: { [weekNumber]: string } }
 */
function getDefaultDayState() {
  return {
    mode: SCHEDULE_MODES.ALL,
    allWeeksType: "FULL_DAY",
    weekTypes: WEEK_NUMBERS.reduce((acc, w) => ({ ...acc, [w]: "FULL_DAY" }), {})
  };
}

function getInitialScheduleState() {
  return DAYS_OF_WEEK.reduce(
    (acc, day) => ({ ...acc, [day.value]: getDefaultDayState() }),
    {}
  );
}

/**
 * Flat server rows -> per-day-of-week state shape the cards use.
 * Rows shape: { dayOfWeek, dayType, weekNumber } ('All' | '1'..'5')
 */
function mapRowsToScheduleState(rows) {
  const state = getInitialScheduleState();

  for (const row of rows ?? []) {
    const dayOfWeek = row.dayOfWeek;
    const dayType = row.dayType;
    const weekNumber = String(row.weekNumber);

    if (!state[dayOfWeek]) continue;

    if (weekNumber === "All") {
      state[dayOfWeek] = {
        ...state[dayOfWeek],
        mode: SCHEDULE_MODES.ALL,
        allWeeksType: dayType
      };
    } else {
      state[dayOfWeek] = {
        ...state[dayOfWeek],
        mode: SCHEDULE_MODES.CUSTOM,
        weekTypes: { ...state[dayOfWeek].weekTypes, [weekNumber]: dayType }
      };
    }
  }

  return state;
}

/**
 * Per-day-of-week state -> flat rows for org_weekly_work_schedule.
 * (day_of_week, day_type, week_number) — tenant_id is attached by the service layer.
 */
function mapScheduleStateToRows(scheduleState) {
  const rows = [];

  for (const day of DAYS_OF_WEEK) {
    const dayState = scheduleState[day.value];
    if (!dayState) continue;

    if (dayState.mode === SCHEDULE_MODES.ALL) {
      rows.push({
        dayOfWeek: day.value,
        dayType: dayState.allWeeksType,
        weekNumber: "All"
      });
    } else {
      for (const weekNumber of WEEK_NUMBERS) {
        rows.push({
          dayOfWeek: day.value,
          dayType: dayState.weekTypes[weekNumber],
          weekNumber
        });
      }
    }
  }

  return rows;
}

export function WeeklyScheduleConfigPage() {
  const navigate = useNavigate();

  const [scheduleState, setScheduleState] = useState(getInitialScheduleState);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    fetchWeeklyWorkSchedule()
    .then((rows) => setScheduleState(mapRowsToScheduleState(rows)))
    .catch(() => setApiError("Failed to load weekly work schedule."))
    .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const updateDay = (dayOfWeek, patch) => {
    setScheduleState((prev) => ({
      ...prev,
      [dayOfWeek]: { ...prev[dayOfWeek], ...patch }
    }));
  };


  const handleModeChange = (dayOfWeek) => (nextMode) => {
    setScheduleState((prev) => {
      const dayState = prev[dayOfWeek];
      if (nextMode === SCHEDULE_MODES.CUSTOM) {
        return {
          ...prev,
          [dayOfWeek]: {
            ...dayState,
            mode: nextMode,
            weekTypes: WEEK_NUMBERS.reduce(
              (acc, w) => ({ ...acc, [w]: dayState.allWeeksType }),
              {}
            )
          }
        };
      }
      return { ...prev, [dayOfWeek]: { ...dayState, mode: nextMode } };
    });
  };

  const handleAllWeeksTypeChange = (dayOfWeek) => (nextType) => {
    updateDay(dayOfWeek, { allWeeksType: nextType });
  };

  const handleWeekTypeChange = (dayOfWeek) => (weekNumber, nextType) => {
    setScheduleState((prev) => ({
      ...prev,
      [dayOfWeek]: {
        ...prev[dayOfWeek],
        weekTypes: { ...prev[dayOfWeek].weekTypes, [weekNumber]: nextType }
      }
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setApiError("");
    try {
      await saveWeeklyWorkSchedule(mapScheduleStateToRows(scheduleState));
      setToast({ message: "Weekly work schedule saved successfully.", type: "success" });
    } catch (err) {
      setApiError(err?.message || "Failed to save weekly work schedule. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AppLayout formLayout="standard">
        <PageNavigation>
          <PageTitleRow back={<PageBackLink to="/" />}>
            <h1><Trans i18nKey="dateTime.weeklyWorkSchedule" /></h1>
          </PageTitleRow>
        </PageNavigation>
        <div className="py-12 text-center text-sm per-file-hint">
          <Trans i18nKey="dateTime.loadingWeeklyWorkSchedule" />
        </div>
      </AppLayout>);

  }

  return (
    <AppLayout formLayout="standard" overlay={<Toast toast={toast} />}>
      <PageNavigation>
        <div className="page-header w-full">
          <PageTitleRow back={<PageBackLink to="/" />}>
            <h1 className="text-[28px] leading-tight font-bold text-foreground">
              <Trans i18nKey="dateTime.weeklyWorkSchedule" />
            </h1>
          </PageTitleRow>
        </div>
      </PageNavigation>

      <div className="w-full">
        {apiError &&
        <div className="flex items-center gap-2 rounded-xl border border-error/30 bg-error/5 px-3.5 py-2.5 text-[13px] text-error mb-4">
            {apiError}
          </div>
        }

        <div className="flex flex-col gap-4">
          {DAYS_OF_WEEK.map((day) =>
          <DayScheduleCard
            key={day.value}
            dayOfWeek={day.value}
            dayLabel={day.label}
            mode={scheduleState[day.value].mode}
            onModeChange={handleModeChange(day.value)}
            allWeeksType={scheduleState[day.value].allWeeksType}
            onAllWeeksTypeChange={handleAllWeeksTypeChange(day.value)}
            weekTypes={scheduleState[day.value].weekTypes}
            onWeekTypeChange={handleWeekTypeChange(day.value)} />

          )}
        </div>

        <div className="flex justify-end gap-2.5 pt-5">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate("/hrms-team/config/weekly-work-schedule")}
            disabled={saving}
            className="h-11 px-5 rounded-xl border-(--color-text-border) text-foreground hover:bg-workspace/40">
            <Trans i18nKey="formActions.cancel" />
          
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent shadow-[0_8px_20px_-8px_rgba(30,51,131,0.5)]">
            
            {saving ? "Saving..." : "Save Schedule"}
          </Button>
        </div>
      </div>
    </AppLayout>);

}
